import {
    useQuery,
    useMutation,
    useInfiniteQuery,
    keepPreviousData,
} from '@tanstack/react-query'
import { api } from '../http'

/**
 * Standard query hook. Forwards the AbortSignal from React Query into axios.
 *
 * @template T
 * @param {{
 *   queryKey: import('@tanstack/react-query').QueryKey,
 *   url: string,
 *   params?: object,
 *   options?: Omit<import('@tanstack/react-query').UseQueryOptions<T>, 'queryKey'|'queryFn'>,
 * }} arg
 */
export function useApiQuery({ queryKey, url, params, options = {} }) {
    return useQuery({
        queryKey,
        queryFn: ({ signal }) => api.get(url, params, { signal }),
        ...options,
    })
}

/**
 * Infinite query for JSON:API link-based pagination (links.next is a full URL).
 *
 * @param {{
 *   queryKey: import('@tanstack/react-query').QueryKey,
 *   url: string,
 *   params?: object,
 *   options?: object,
 * }} arg
 */
export function useApiInfiniteQuery({ queryKey, url, params, options = {} }) {
    return useInfiniteQuery({
        queryKey,
        queryFn: ({ pageParam, signal }) => {
            // pageParam is the full `links.next` URL once we're past page 1.
            if (pageParam) return api.get(pageParam, undefined, { signal })
            return api.get(url, params, { signal })
        },
        initialPageParam: null,
        getNextPageParam: (lastPage) => lastPage?.links?.next ?? undefined,
        getPreviousPageParam: (firstPage) =>
            firstPage?.links?.prev ?? undefined,
        placeholderData: keepPreviousData,
        ...options,
    })
}

/**
 * Infinite query for page-number pagination (page[number]=N / meta.last_page).
 *
 * @param {{
 *   queryKey: import('@tanstack/react-query').QueryKey,
 *   url: string,
 *   buildPageParams: (pageNumber: number) => object,
 *   options?: object,
 * }} arg
 */
export function useApiPagedInfiniteQuery({
    queryKey,
    url,
    buildPageParams,
    options = {},
}) {
    return useInfiniteQuery({
        queryKey,
        queryFn: ({ pageParam = 1, signal }) =>
            api.get(url, buildPageParams(pageParam), { signal }),
        initialPageParam: 1,
        getNextPageParam: (lastPage, _all, lastPageParam) => {
            const meta = lastPage?.meta
            if (!meta) return undefined
            const last =
                meta.last_page ??
                (meta.per_page && meta.total
                    ? Math.ceil(meta.total / meta.per_page)
                    : undefined)
            if (!last) return undefined
            return lastPageParam < last ? lastPageParam + 1 : undefined
        },
        placeholderData: keepPreviousData,
        ...options,
    })
}

/**
 * Thin re-export so hook files only import from one place.
 */
export function useApiMutation(options) {
    return useMutation(options)
}

export { keepPreviousData }
